import { DataItem } from '../../types';
import { StorageProvider, StorageProviderConfig, QueryOptions } from './types';
import { generateId } from '../uuidUtils';
// Import SQL.js dynamically to avoid Parcel bundling issues
let initSqlJs: any;

export class SQLJSProvider implements StorageProvider {
    private db: any = null;
    private SQL: any = null;
    private dbName: string;
    private storeName: string;
    private initialized: boolean = false;
    private initializationPromise: Promise<void> | null = null;

    constructor(config?: StorageProviderConfig) {
        this.dbName = config?.dbName || 'InfiniteListDB';
        this.storeName = config?.storeName || 'items';
    }

    async initialize(): Promise<void> {
        console.log('SQLJSProvider.initialize() called', { initialized: this.initialized, hasPromise: !!this.initializationPromise });
        
        if (this.initialized) {
            console.log('Already initialized, returning');
            return;
        }

        // Prevent race conditions by reusing the same initialization promise
        if (this.initializationPromise) {
            console.log('Reusing existing initialization promise');
            return this.initializationPromise;
        }

        console.log('Starting new SQLJSProvider initialization');
        this.initializationPromise = this.doInitialize();
        return this.initializationPromise;
    }

    private async doInitialize(): Promise<void> {
        try {
            console.log('SQLJSProvider.doInitialize() starting');
            
            // Dynamically import SQL.js to avoid Parcel bundling issues
            if (!initSqlJs) {
                console.log('Dynamically importing SQL.js');
                try {
                    const sqlModule = await import('sql.js');
                    initSqlJs = sqlModule.default || sqlModule;
                    
                    if (typeof initSqlJs !== 'function') {
                        throw new Error('SQL.js module did not export a valid initialization function');
                    }
                } catch (importError) {
                    console.error('Failed to import SQL.js:', importError);
                    throw new Error(`SQL.js import failed: ${importError instanceof Error ? importError.message : String(importError)}`);
                }
            }
            
            // Initialize SQL.js
            console.log('Initializing SQL.js library');
            this.SQL = await initSqlJs({
                locateFile: file => `https://sql.js.org/dist/${file}`
            });
            
            if (!this.SQL || typeof this.SQL.Database !== 'function') {
                throw new Error('SQL.js library initialization failed - Database constructor not available');
            }
            
            console.log('SQL.js library initialized successfully');

            // Try to load existing database from OPFS
            console.log('Loading from OPFS');
            const data = await this.loadFromOPFS();
            if (data && data.byteLength > 0) {
                console.log(`Creating database from OPFS data (${data.byteLength} bytes)`);
                try {
                    this.db = new this.SQL.Database(data);
                    // Ensure schema exists and is valid for loaded databases
                    this.createSchema();
                } catch (dbLoadError) {
                    console.warn('Failed to load database from OPFS data, creating new database:', dbLoadError);
                    this.db = new this.SQL.Database();
                    this.createSchema();
                }
            } else {
                console.log('No existing OPFS data found, creating new database and schema');
                this.db = new this.SQL.Database();
                this.createSchema();
            }

            this.initialized = true;
            console.log('SQLJSProvider initialization completed successfully');
        } catch (error) {
            console.error('SQLJSProvider initialization failed:', error);
            this.initializationPromise = null; // Reset so initialization can be retried
            throw new Error(`Failed to initialize SQL.js: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    private createSchema(): void {
        try {
            // First check if the table exists
            const tableExistsQuery = `SELECT name FROM sqlite_master WHERE type='table' AND name='${this.storeName}'`;
            const result = this.db.exec(tableExistsQuery);
            const tableExists = result.length > 0 && result[0].values.length > 0;
            
            if (!tableExists) {
                console.log(`Creating table ${this.storeName}`);
                this.db.run(`
                    CREATE TABLE ${this.storeName} (
                        id TEXT PRIMARY KEY,
                        name TEXT NOT NULL,
                        description TEXT,
                        imageUrl TEXT,
                        category TEXT,
                        timestamp INTEGER,
                        data TEXT
                    )
                `);
            } else {
                console.log(`Table ${this.storeName} already exists`);
                
                // Verify table structure by checking if we can query it
                try {
                    this.db.exec(`SELECT COUNT(*) FROM ${this.storeName} LIMIT 1`);
                    console.log(`Table ${this.storeName} is accessible`);
                } catch (queryError) {
                    console.error(`Table ${this.storeName} exists but is not queryable:`, queryError);
                    // Drop and recreate the table if it's corrupted
                    this.db.run(`DROP TABLE IF EXISTS ${this.storeName}`);
                    this.db.run(`
                        CREATE TABLE ${this.storeName} (
                            id TEXT PRIMARY KEY,
                            name TEXT NOT NULL,
                            description TEXT,
                            imageUrl TEXT,
                            category TEXT,
                            timestamp INTEGER,
                            data TEXT
                        )
                    `);
                    console.log(`Recreated table ${this.storeName}`);
                }
            }

            // Create indexes for better performance
            this.db.run(`CREATE INDEX IF NOT EXISTS idx_category ON ${this.storeName}(category)`);
            this.db.run(`CREATE INDEX IF NOT EXISTS idx_timestamp ON ${this.storeName}(timestamp)`);
            this.db.run(`CREATE INDEX IF NOT EXISTS idx_name ON ${this.storeName}(name)`);
            
            console.log(`Schema creation completed for ${this.storeName}`);
        } catch (error) {
            console.error('Failed to create schema:', error);
            throw new Error(`Schema creation failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    private buildQuery(options?: QueryOptions, isCountQuery: boolean = false): { query: string; params: any[] } {
        const whereConditions: string[] = [];
        const params: any[] = [];
        let orderBy = '';

        // Handle search
        if (options?.search && options.search.trim()) {
            whereConditions.push('(name LIKE ? OR description LIKE ?)');
            const searchTerm = `%${options.search.trim()}%`;
            params.push(searchTerm, searchTerm);
        }

        // Handle category filter (supports comma-separated values)
        if (options?.filterBy && options.filterBy.trim()) {
            const categories = options.filterBy.split(',').map(c => c.trim()).filter(c => c);
            if (categories.length === 1) {
                whereConditions.push('category = ?');
                params.push(categories[0]);
            } else if (categories.length > 1) {
                whereConditions.push(`category IN (${categories.map(() => '?').join(', ')})`);
                params.push(...categories);
            }
        }

        // Handle sorting (only for non-count queries)
        if (!isCountQuery && options?.sortBy && options?.sortOrder) {
            const sortBy = options.sortBy;
            const sortOrder = options.sortOrder.toUpperCase();
            orderBy = `ORDER BY ${sortBy} ${sortOrder}`;
        } else if (!isCountQuery) {
            // Default sorting
            orderBy = 'ORDER BY id';
        }

        // Build the query
        let query = '';
        if (whereConditions.length > 0) {
            query = `WHERE ${whereConditions.join(' AND ')}`;
        }
        if (orderBy) {
            query += ` ${orderBy}`;
        }

        return { query, params };
    }

    async seedData(count: number, days?: number): Promise<void> {
        console.log(`SQLJSProvider.seedData called with count=${count}, days=${days}`);
        if (!this.db) throw new Error('SQL.js not initialized');

        // Clear existing data
        this.db.run(`DELETE FROM ${this.storeName}`);
        console.log('Cleared existing data');

        // Use transaction for better performance
        this.db.run('BEGIN TRANSACTION');

        // First, update the schema to include a data column
        try {
            this.db.run(`ALTER TABLE ${this.storeName} ADD COLUMN data TEXT`);
        } catch (e) {
            // Column might already exist
        }

        const stmt = this.db.prepare(`
            INSERT INTO ${this.storeName} (id, name, description, imageUrl, category, timestamp, data)
            VALUES (?, ?, ?, ?, ?, ?, ?)
        `);

        // If count is small, do simple seeding
        if (count < 100) {
            console.log('Using seedSimpleData path');
            this.seedSimpleData(stmt, count);
        } else {
            // For larger counts, seed realistic day-based patterns
            // Use days parameter if provided, otherwise default to 180
            console.log(`Using seedDayBasedData path with ${days || 180} days`);
            this.seedDayBasedData(stmt, days || 180);
        }

        stmt.free();
        this.db.run('COMMIT');

        // Save to OPFS
        await this.saveToOPFS();
    }

    private seedSimpleData(stmt: any, count: number): void {
        const mealTemplates = this.getMealTemplates();
        const profileTemplates = this.getProfileTemplates();

        let itemCount = 0;
        const itemsPerCategory = Math.floor(count / 3);
        const remainder = count % 3;

        // Add workouts using new generation system
        for (let i = 0; i < itemsPerCategory + (remainder > 0 ? 1 : 0) && itemCount < count; i++) {
            const workoutTypes = ['upper', 'lower', 'cardio'];
            const workoutType = workoutTypes[i % 3];
            const weekNumber = Math.floor(i / 6); // Simulate week progression
            const workout = this.generateWorkoutForDay(workoutType, weekNumber);
            
            stmt.run([
                generateId(),
                workout.name,
                workout.description,
                workout.imageUrl,
                'workout',
                Date.now() - Math.floor(Math.random() * 21 * 24 * 60 * 60 * 1000),
                JSON.stringify(workout.data)
            ]);
            itemCount++;
        }

        // Add meals
        for (let i = 0; i < itemsPerCategory + (remainder > 1 ? 1 : 0) && itemCount < count; i++) {
            const meal = mealTemplates[i % mealTemplates.length];
            stmt.run([
                generateId(),
                meal.name,
                meal.description,
                meal.imageUrl,
                'meal',
                Date.now() - Math.floor(Math.random() * 21 * 24 * 60 * 60 * 1000),
                JSON.stringify(meal.data)
            ]);
            itemCount++;
        }

        // Add profiles
        for (let i = 0; i < itemsPerCategory && itemCount < count; i++) {
            const profile = profileTemplates[i % profileTemplates.length];
            stmt.run([
                generateId(),
                profile.name,
                profile.description,
                profile.imageUrl,
                'profile',
                Date.now() - Math.floor(Math.random() * 21 * 24 * 60 * 60 * 1000),
                JSON.stringify(profile.data)
            ]);
            itemCount++;
        }
    }

    private seedDayBasedData(stmt: any, days: number = 180): void {
        console.log(`Starting seedDayBasedData for ${days} days`);
        // Don't use old templates - we'll generate workouts dynamically
        const profileTemplates = this.getProfileTemplates();
        
        const now = Date.now();
        const dayInMs = 24 * 60 * 60 * 1000;
        
        // 6-day workout split: Mon/Thu = Upper Body, Tue/Fri = Lower Body, Wed/Sat = Cardio, Sun = Rest
        const workoutSchedule: { [key: number]: string } = {
            1: 'upper', // Monday
            2: 'lower', // Tuesday  
            3: 'cardio', // Wednesday
            4: 'upper', // Thursday
            5: 'lower', // Friday
            6: 'cardio', // Saturday
            0: 'rest' // Sunday
        };
        
        // Meal schedule - 4 meals per day (breakfast, lunch, dinner, snack)
        const mealsPerDay = {
            breakfast: 1,
            lunch: 1, 
            dinner: 1,
            snack: 1
        };

        // Seed specified days of data
        for (let daysAgo = 0; daysAgo < days; daysAgo++) {
            const dayDate = new Date(now - (daysAgo * dayInMs));
            const dayOfWeek = dayDate.getDay();
            const weekNumber = Math.floor(daysAgo / 7); // For progressive overload
            
            const workoutType = workoutSchedule[dayOfWeek];
            
            // Add workouts (except rest days)
            if (workoutType !== 'rest') {
                const workout = this.generateWorkoutForDay(workoutType, weekNumber);
                const workoutTime = new Date(dayDate).setHours(6 + Math.floor(Math.random() * 3), Math.floor(Math.random() * 60), 0, 0);
                
                // Log workout details for debugging
                if (daysAgo === 0) {
                    console.log(`Generated ${workoutType} workout for day ${daysAgo}:`, {
                        name: workout.name,
                        exercises: workout.data.exercises.length,
                        sets: workout.data.exercises.filter(e => e.setNumber).length
                    });
                }
                
                stmt.run([
                    generateId(),
                    workout.name,
                    workout.description,
                    workout.imageUrl,
                    'workout',
                    workoutTime,
                    JSON.stringify(workout.data)
                ]);
            }
            
            // Add meals for each day (2000 calorie diet)
            const dailyMeals = this.generate2000CalorieMeals();
            
            // Breakfast (~500 calories)
            const breakfastTime = new Date(dayDate).setHours(7 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0, 0);
            stmt.run([
                generateId(),
                dailyMeals.breakfast.name,
                dailyMeals.breakfast.description,
                dailyMeals.breakfast.imageUrl,
                'meal',
                breakfastTime,
                JSON.stringify(dailyMeals.breakfast.data)
            ]);
            
            // Lunch (~600 calories)
            const lunchTime = new Date(dayDate).setHours(12 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0, 0);
            stmt.run([
                generateId(),
                dailyMeals.lunch.name,
                dailyMeals.lunch.description,
                dailyMeals.lunch.imageUrl,
                'meal',
                lunchTime,
                JSON.stringify(dailyMeals.lunch.data)
            ]);
            
            // Dinner (~700 calories)
            const dinnerTime = new Date(dayDate).setHours(18 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0, 0);
            stmt.run([
                generateId(),
                dailyMeals.dinner.name,
                dailyMeals.dinner.description,
                dailyMeals.dinner.imageUrl,
                'meal',
                dinnerTime,
                JSON.stringify(dailyMeals.dinner.data)
            ]);
            
            // Snack (~200 calories)  
            const snackTime = new Date(dayDate).setHours(15 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0, 0);
            stmt.run([
                generateId(),
                dailyMeals.snack.name,
                dailyMeals.snack.description,
                dailyMeals.snack.imageUrl,
                'meal',
                snackTime,
                JSON.stringify(dailyMeals.snack.data)
            ]);
        }
        
        // Add 3 profiles
        for (let i = 0; i < 3; i++) {
            const profile = profileTemplates[i];
            stmt.run([
                generateId(),
                profile.name,
                profile.description,
                profile.imageUrl,
                'profile',
                now - (i * 7 * dayInMs), // One profile per week
                JSON.stringify(profile.data)
            ]);
        }
    }

    private generateWorkoutForDay(type: string, weekNumber: number) {
        const baseWeight = {
            upper: { bench: 135, overhead: 95, row: 115, curl: 30, tricep: 25 },
            lower: { squat: 185, deadlift: 225, lunge: 25, calf: 200, leg_curl: 80 }
        };
        
        // Progressive overload: add 2.5-5 lbs every 2 weeks
        const weightIncrease = Math.floor(weekNumber / 2) * 5;
        
        if (type === 'upper') {
            // Upper body exercises - 10 distinct exercises, 3 entries each = 30 total
            const upperExercises = [
                { name: "Bench Press", baseWeight: baseWeight.upper.bench },
                { name: "Barbell Rows", baseWeight: baseWeight.upper.row },
                { name: "Overhead Press", baseWeight: baseWeight.upper.overhead },
                { name: "Pull-ups", baseWeight: 0 }, // bodyweight
                { name: "Incline Dumbbell Press", baseWeight: 40 },
                { name: "Lat Pulldowns", baseWeight: 100 },
                { name: "Lateral Raises", baseWeight: 15 },
                { name: "Bicep Curls", baseWeight: baseWeight.upper.curl },
                { name: "Tricep Extensions", baseWeight: baseWeight.upper.tricep },
                { name: "Face Pulls", baseWeight: 50 }
            ];

            const exercises = [];
            // Create 3 entries for each exercise
            upperExercises.forEach(exercise => {
                for (let set = 1; set <= 3; set++) {
                    exercises.push({
                        name: exercise.name,
                        setNumber: set,
                        reps: exercise.name === "Lateral Raises" ? 15 : exercise.name === "Face Pulls" ? 15 : 10,
                        ...(exercise.baseWeight > 0 ? { weight: exercise.baseWeight + weightIncrease } : {})
                    });
                }
            });

            return {
                name: `Upper Body Strength - Week ${weekNumber + 1}`,
                description: "Complete upper body workout: 10 exercises × 3 sets each = 30 total exercises",
                imageUrl: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&h=300&fit=crop",
                data: {
                    exercises: exercises,
                    duration: 75,
                    calories: 400,
                    type: "strength",
                    difficulty: "intermediate"
                }
            };
        } else if (type === 'lower') {
            // Lower body exercises - 10 distinct exercises, 3 entries each = 30 total
            const lowerExercises = [
                { name: "Squats", baseWeight: baseWeight.lower.squat },
                { name: "Romanian Deadlifts", baseWeight: baseWeight.lower.deadlift },
                { name: "Bulgarian Split Squats", baseWeight: baseWeight.lower.lunge },
                { name: "Hip Thrusts", baseWeight: 95 },
                { name: "Leg Press", baseWeight: 270 },
                { name: "Leg Curls", baseWeight: baseWeight.lower.leg_curl },
                { name: "Leg Extensions", baseWeight: 90 },
                { name: "Walking Lunges", baseWeight: 20 },
                { name: "Calf Raises", baseWeight: baseWeight.lower.calf },
                { name: "Glute Bridges", baseWeight: 0 } // bodyweight
            ];

            const exercises = [];
            // Create 3 entries for each exercise
            lowerExercises.forEach(exercise => {
                for (let set = 1; set <= 3; set++) {
                    exercises.push({
                        name: exercise.name,
                        setNumber: set,
                        reps: exercise.name === "Walking Lunges" ? 20 : exercise.name === "Calf Raises" ? 20 : exercise.name === "Leg Press" ? 15 : 12,
                        ...(exercise.baseWeight > 0 ? { weight: exercise.baseWeight + (exercise.name === "Leg Press" ? weightIncrease * 2 : weightIncrease) } : {})
                    });
                }
            });

            return {
                name: `Lower Body Strength - Week ${weekNumber + 1}`,
                description: "Complete lower body workout: 10 exercises × 3 sets each = 30 total exercises",
                imageUrl: "https://images.unsplash.com/photo-1434682881908-b43d0467b798?w=400&h=300&fit=crop",
                data: {
                    exercises: exercises,
                    duration: 80,
                    calories: 450,
                    type: "strength",
                    difficulty: "intermediate"
                }
            };
        } else if (type === 'cardio') {
            // Cardio day - both running AND swimming
            return {
                name: "Cardio Session",
                description: "Combined running and swimming cardio workout",
                imageUrl: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Running", duration: 1800, distance: 3 }, // 30 minutes
                        { name: "Swimming", duration: 1800, distance: 0.75 } // 30 minutes
                    ],
                    duration: 60,
                    calories: 600,
                    type: "cardio",
                    difficulty: "intermediate"
                }
            };
        }
        
        // Default fallback (should never reach here)
        return {
            name: "General Workout",
            description: "General fitness workout",
            imageUrl: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop",
            data: {
                exercises: [{ name: "General Exercise", duration: 1800 }],
                duration: 30,
                calories: 200,
                type: "mixed",
                difficulty: "beginner"
            }
        };
    }

    private generate2000CalorieMeals() {
        const breakfastOptions = [
            {
                name: "High-Protein Breakfast",
                description: "Scrambled eggs with avocado toast and Greek yogurt",
                imageUrl: "https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=400&h=300&fit=crop",
                data: {
                    calories: 500,
                    protein: 30,
                    carbs: 40,
                    fat: 25,
                    fiber: 8,
                    ingredients: ["3 large eggs", "2 slices whole grain bread", "1/2 avocado", "1 cup Greek yogurt", "1 tbsp olive oil"],
                    portions: [
                        { name: "Scrambled eggs", amount: "3 large", calories: 210, protein: 18, carbs: 2, fat: 15 },
                        { name: "Whole grain toast", amount: "2 slices", calories: 160, protein: 8, carbs: 30, fat: 2 },
                        { name: "Avocado", amount: "1/2 medium", calories: 120, protein: 2, carbs: 6, fat: 11 },
                        { name: "Greek yogurt", amount: "1/2 cup", calories: 10, protein: 2, carbs: 2, fat: 0 }
                    ],
                    mealType: "breakfast",
                    prepTime: 15
                }
            },
            {
                name: "Oatmeal Power Bowl",
                description: "Steel-cut oats with berries, nuts, and protein powder",
                imageUrl: "https://images.unsplash.com/photo-1517673408731-4a5e6ca1cf5f?w=400&h=300&fit=crop",
                data: {
                    calories: 480,
                    protein: 25,
                    carbs: 65,
                    fat: 12,
                    fiber: 10,
                    ingredients: ["1 cup steel-cut oats", "1 scoop protein powder", "1/2 cup berries", "1 tbsp almonds", "1 tbsp honey"],
                    portions: [
                        { name: "Steel-cut oats", amount: "1 cup cooked", calories: 300, protein: 10, carbs: 54, fat: 5 },
                        { name: "Protein powder", amount: "1 scoop", calories: 120, protein: 24, carbs: 3, fat: 1 },
                        { name: "Mixed berries", amount: "1/2 cup", calories: 40, protein: 1, carbs: 10, fat: 0 },
                        { name: "Almonds", amount: "1 tbsp", calories: 20, protein: 1, carbs: 1, fat: 6 }
                    ],
                    mealType: "breakfast",
                    prepTime: 10
                }
            }
        ];

        const lunchOptions = [
            {
                name: "Power Lunch Bowl",
                description: "Grilled chicken with quinoa, vegetables, and tahini dressing",
                imageUrl: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop",
                data: {
                    calories: 600,
                    protein: 45,
                    carbs: 50,
                    fat: 20,
                    fiber: 12,
                    ingredients: ["6 oz grilled chicken", "1 cup cooked quinoa", "mixed vegetables", "2 tbsp tahini", "olive oil"],
                    portions: [
                        { name: "Grilled chicken breast", amount: "6 oz", calories: 280, protein: 35, carbs: 0, fat: 15 },
                        { name: "Quinoa", amount: "1 cup cooked", calories: 220, protein: 8, carbs: 40, fat: 4 },
                        { name: "Mixed vegetables", amount: "2 cups", calories: 60, protein: 2, carbs: 12, fat: 0 },
                        { name: "Tahini dressing", amount: "2 tbsp", calories: 40, protein: 0, carbs: 0, fat: 1 }
                    ],
                    mealType: "lunch",
                    prepTime: 20
                }
            }
        ];

        const dinnerOptions = [
            {
                name: "Balanced Dinner Plate",
                description: "Baked salmon with sweet potato and steamed broccoli",
                imageUrl: "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=400&h=300&fit=crop",
                data: {
                    calories: 700,
                    protein: 50,
                    carbs: 60,
                    fat: 25,
                    fiber: 15,
                    ingredients: ["8 oz salmon fillet", "1 large sweet potato", "2 cups broccoli", "olive oil", "herbs"],
                    portions: [
                        { name: "Baked salmon", amount: "8 oz", calories: 400, protein: 45, carbs: 0, fat: 22 },
                        { name: "Roasted sweet potato", amount: "1 large", calories: 180, protein: 4, carbs: 40, fat: 0 },
                        { name: "Steamed broccoli", amount: "2 cups", calories: 60, protein: 1, carbs: 12, fat: 0 },
                        { name: "Olive oil", amount: "1 tbsp", calories: 60, protein: 0, carbs: 0, fat: 3 }
                    ],
                    mealType: "dinner",
                    prepTime: 25
                }
            }
        ];

        const snackOptions = [
            {
                name: "Protein Snack",
                description: "Apple slices with almond butter and a protein shake",
                imageUrl: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",
                data: {
                    calories: 200,
                    protein: 15,
                    carbs: 20,
                    fat: 8,
                    fiber: 5,
                    ingredients: ["1 medium apple", "2 tbsp almond butter"],
                    portions: [
                        { name: "Apple", amount: "1 medium", calories: 80, protein: 0, carbs: 20, fat: 0 },
                        { name: "Almond butter", amount: "2 tbsp", calories: 120, protein: 15, carbs: 0, fat: 8 }
                    ],
                    mealType: "snack",
                    prepTime: 5
                }
            }
        ];

        return {
            breakfast: breakfastOptions[Math.floor(Math.random() * breakfastOptions.length)],
            lunch: lunchOptions[Math.floor(Math.random() * lunchOptions.length)],
            dinner: dinnerOptions[Math.floor(Math.random() * dinnerOptions.length)],
            snack: snackOptions[Math.floor(Math.random() * snackOptions.length)]
        };
    }

    private getWorkoutTemplates() {
        return [
            // Short workout - 2 exercises
            {
                name: "Quick Cardio",
                description: "Brief cardio session for busy days",
                imageUrl: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Jumping Jacks", setNumber: 1, reps: 30 },
                        { name: "Jumping Jacks", setNumber: 2, reps: 30 },
                        { name: "Jumping Jacks", setNumber: 3, reps: 30 },
                        { name: "Running in Place", duration: 300 }
                    ],
                    duration: 10,
                    calories: 80,
                    type: "cardio",
                    difficulty: "beginner"
                }
            },
            // Medium workout - 3 exercises
            {
                name: "Core Blast",
                description: "Focused core strengthening workout",
                imageUrl: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Plank", duration: 60 },
                        { name: "Crunches", setNumber: 1, reps: 20 },
                        { name: "Crunches", setNumber: 2, reps: 20 },
                        { name: "Crunches", setNumber: 3, reps: 20 },
                        { name: "Russian Twists", setNumber: 1, reps: 30 },
                        { name: "Russian Twists", setNumber: 2, reps: 30 },
                        { name: "Russian Twists", setNumber: 3, reps: 30 }
                    ],
                    duration: 15,
                    calories: 100,
                    type: "strength",
                    difficulty: "intermediate"
                }
            },
            // Standard workout - 5 exercises
            {
                name: "Upper Body Strength",
                description: "Complete upper body workout targeting chest, back, shoulders, and arms",
                imageUrl: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Bench Press", setNumber: 1, reps: 8, weight: 135 },
                        { name: "Bench Press", setNumber: 2, reps: 8, weight: 135 },
                        { name: "Bench Press", setNumber: 3, reps: 8, weight: 135 },
                        { name: "Bench Press", setNumber: 4, reps: 8, weight: 135 },
                        { name: "Pull-ups", setNumber: 1, reps: 10 },
                        { name: "Pull-ups", setNumber: 2, reps: 10 },
                        { name: "Pull-ups", setNumber: 3, reps: 10 },
                        { name: "Overhead Press", setNumber: 1, reps: 10, weight: 95 },
                        { name: "Overhead Press", setNumber: 2, reps: 10, weight: 95 },
                        { name: "Overhead Press", setNumber: 3, reps: 10, weight: 95 },
                        { name: "Bicep Curls", setNumber: 1, reps: 12, weight: 30 },
                        { name: "Bicep Curls", setNumber: 2, reps: 12, weight: 30 },
                        { name: "Bicep Curls", setNumber: 3, reps: 12, weight: 30 },
                        { name: "Tricep Dips", setNumber: 1, reps: 12 },
                        { name: "Tricep Dips", setNumber: 2, reps: 12 },
                        { name: "Tricep Dips", setNumber: 3, reps: 12 }
                    ],
                    duration: 45,
                    calories: 300,
                    type: "strength",
                    difficulty: "intermediate"
                }
            },
            // Single exercise workout
            {
                name: "Morning Run",
                description: "5-mile run through the park at moderate pace",
                imageUrl: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Running", duration: 2400, distance: 5 }
                    ],
                    duration: 40,
                    calories: 450,
                    type: "cardio",
                    difficulty: "intermediate"
                }
            },
            // Standard workout - 5 exercises
            {
                name: "Leg Day",
                description: "Intense lower body workout focusing on quads, hamstrings, and glutes",
                imageUrl: "https://images.unsplash.com/photo-1434682881908-b43d0467b798?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Squats", setNumber: 1, reps: 10, weight: 185 },
                        { name: "Squats", setNumber: 2, reps: 10, weight: 185 },
                        { name: "Squats", setNumber: 3, reps: 10, weight: 185 },
                        { name: "Squats", setNumber: 4, reps: 10, weight: 185 },
                        { name: "Romanian Deadlifts", setNumber: 1, reps: 12, weight: 135 },
                        { name: "Romanian Deadlifts", setNumber: 2, reps: 12, weight: 135 },
                        { name: "Romanian Deadlifts", setNumber: 3, reps: 12, weight: 135 },
                        { name: "Leg Press", setNumber: 1, reps: 15, weight: 270 },
                        { name: "Leg Press", setNumber: 2, reps: 15, weight: 270 },
                        { name: "Leg Press", setNumber: 3, reps: 15, weight: 270 },
                        { name: "Walking Lunges", setNumber: 1, reps: 20, weight: 40 },
                        { name: "Walking Lunges", setNumber: 2, reps: 20, weight: 40 },
                        { name: "Walking Lunges", setNumber: 3, reps: 20, weight: 40 },
                        { name: "Calf Raises", setNumber: 1, reps: 15, weight: 90 },
                        { name: "Calf Raises", setNumber: 2, reps: 15, weight: 90 },
                        { name: "Calf Raises", setNumber: 3, reps: 15, weight: 90 },
                        { name: "Calf Raises", setNumber: 4, reps: 15, weight: 90 }
                    ],
                    duration: 60,
                    calories: 400,
                    type: "strength",
                    difficulty: "advanced"
                }
            },
            // Medium workout - 4 exercises
            {
                name: "Yoga Flow",
                description: "Vinyasa yoga session for flexibility and mindfulness",
                imageUrl: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Sun Salutations", duration: 300 },
                        { name: "Warrior Sequence", duration: 600 },
                        { name: "Balance Poses", duration: 480 },
                        { name: "Cool Down Stretches", duration: 420 }
                    ],
                    duration: 30,
                    calories: 150,
                    type: "flexibility",
                    difficulty: "beginner"
                }
            },
            // Standard workout - 5 exercises
            {
                name: "HIIT Circuit",
                description: "High-intensity interval training for maximum calorie burn",
                imageUrl: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Burpees", sets: 4, reps: 10 },
                        { name: "Mountain Climbers", sets: 4, duration: 30 },
                        { name: "Jump Squats", sets: 4, reps: 15 },
                        { name: "Plank Jacks", sets: 4, duration: 30 },
                        { name: "High Knees", sets: 4, duration: 30 }
                    ],
                    duration: 25,
                    calories: 350,
                    type: "cardio",
                    difficulty: "advanced"
                }
            },
            // Long workout - 8 exercises
            {
                name: "Full Body Strength",
                description: "Comprehensive full-body strength training session",
                imageUrl: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Deadlifts", sets: 4, reps: 6, weight: 225 },
                        { name: "Bench Press", sets: 4, reps: 8, weight: 155 },
                        { name: "Barbell Rows", sets: 4, reps: 8, weight: 135 },
                        { name: "Overhead Press", sets: 3, reps: 10, weight: 95 },
                        { name: "Pull-ups", sets: 3, reps: 8 },
                        { name: "Dips", sets: 3, reps: 12 },
                        { name: "Barbell Curls", sets: 3, reps: 12, weight: 65 },
                        { name: "Close-Grip Bench Press", sets: 3, reps: 10, weight: 115 }
                    ],
                    duration: 75,
                    calories: 450,
                    type: "strength",
                    difficulty: "advanced"
                }
            },
            // Very long workout - 10 exercises
            {
                name: "Epic Push/Pull Split",
                description: "Intense push and pull workout for serious lifters",
                imageUrl: "https://images.unsplash.com/photo-1434682881908-b43d0467b798?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Bench Press", sets: 4, reps: 8, weight: 185 },
                        { name: "Incline Dumbbell Press", sets: 3, reps: 10, weight: 70 },
                        { name: "Pull-ups", sets: 4, reps: 8 },
                        { name: "Barbell Rows", sets: 4, reps: 8, weight: 155 },
                        { name: "Overhead Press", sets: 3, reps: 10, weight: 115 },
                        { name: "Lateral Raises", sets: 3, reps: 15, weight: 20 },
                        { name: "Tricep Dips", sets: 3, reps: 12 },
                        { name: "Barbell Curls", sets: 3, reps: 12, weight: 75 },
                        { name: "Face Pulls", sets: 3, reps: 15, weight: 50 },
                        { name: "Diamond Push-ups", sets: 3, reps: 10 }
                    ],
                    duration: 90,
                    calories: 550,
                    type: "strength",
                    difficulty: "advanced"
                }
            },
            // Long cardio workout - 6 exercises
            {
                name: "Cardio Circuit Training",
                description: "High-energy circuit training with varied cardio exercises",
                imageUrl: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=300&fit=crop",
                data: {
                    exercises: [
                        { name: "Treadmill Run", duration: 600, distance: 2 },
                        { name: "Rowing Machine", duration: 300, distance: 1000 },
                        { name: "Stationary Bike", duration: 480, distance: 3 },
                        { name: "Elliptical", duration: 360 },
                        { name: "Stair Climber", duration: 240 },
                        { name: "Battle Ropes", sets: 3, duration: 30 }
                    ],
                    duration: 50,
                    calories: 620,
                    type: "cardio",
                    difficulty: "intermediate"
                }
            }
        ];
    }

    private getMealTemplates() {
        return [
            // Complex breakfast with many portions
            {
                name: "Breakfast",
                description: "Power breakfast with protein smoothie bowl, toast, and almond butter",
                imageUrl: "",
                data: {
                    calories: 620,
                    protein: 35,
                    carbs: 82,
                    fat: 18,
                    fiber: 14,
                    ingredients: ["Protein powder", "Mixed berries", "Banana", "Almond milk", "Granola", "Chia seeds", "Whole grain toast", "Almond butter", "Greek yogurt", "Honey"],
                    portions: [
                        { name: "Vanilla protein powder", amount: "1 scoop", calories: 120, protein: 25, carbs: 3, fat: 1 },
                        { name: "Mixed berries", amount: "3/4 cup", calories: 60, protein: 1, carbs: 15, fat: 0 },
                        { name: "Fresh banana", amount: "1 medium", calories: 105, protein: 1, carbs: 27, fat: 0 },
                        { name: "Almond milk", amount: "1/2 cup", calories: 20, protein: 1, carbs: 2, fat: 2 },
                        { name: "Greek yogurt", amount: "1/2 cup", calories: 75, protein: 13, carbs: 6, fat: 0 },
                        { name: "Granola", amount: "1/4 cup", calories: 120, protein: 3, carbs: 22, fat: 4 },
                        { name: "Chia seeds", amount: "1 tbsp", calories: 60, protein: 2, carbs: 5, fat: 4 },
                        { name: "Whole grain toast", amount: "2 slices", calories: 160, protein: 8, carbs: 28, fat: 2 },
                        { name: "Almond butter", amount: "2 tbsp", calories: 190, protein: 7, carbs: 6, fat: 18 },
                        { name: "Honey drizzle", amount: "1 tsp", calories: 21, protein: 0, carbs: 6, fat: 0 },
                        { name: "Coffee with milk", amount: "1 cup", calories: 25, protein: 1, carbs: 3, fat: 1 }
                    ],
                    mealType: "breakfast",
                    prepTime: 15
                }
            },
            // Simple breakfast with few portions
            {
                name: "Breakfast",
                description: "Quick breakfast with oatmeal and coffee",
                imageUrl: "",
                data: {
                    calories: 320,
                    protein: 12,
                    carbs: 58,
                    fat: 6,
                    fiber: 8,
                    ingredients: ["Oats", "Milk", "Banana", "Coffee"],
                    portions: [
                        { name: "Instant oatmeal", amount: "1 packet", calories: 150, protein: 4, carbs: 32, fat: 3 },
                        { name: "2% milk", amount: "1/2 cup", calories: 60, protein: 4, carbs: 6, fat: 3 },
                        { name: "Sliced banana", amount: "1/2 medium", calories: 52, protein: 1, carbs: 13, fat: 0 },
                        { name: "Black coffee", amount: "1 cup", calories: 2, protein: 0, carbs: 0, fat: 0 }
                    ],
                    mealType: "breakfast",
                    prepTime: 5
                }
            },
            // Minimal breakfast
            {
                name: "Breakfast",
                description: "Simple coffee and toast",
                imageUrl: "",
                data: {
                    calories: 180,
                    protein: 6,
                    carbs: 28,
                    fat: 5,
                    fiber: 3,
                    ingredients: ["Bread", "Butter", "Coffee"],
                    portions: [
                        { name: "Whole wheat toast", amount: "2 slices", calories: 160, protein: 6, carbs: 28, fat: 3 },
                        { name: "Butter", amount: "1 tsp", calories: 34, protein: 0, carbs: 0, fat: 4 },
                        { name: "Black coffee", amount: "1 cup", calories: 2, protein: 0, carbs: 0, fat: 0 }
                    ],
                    mealType: "breakfast",
                    prepTime: 3
                }
            },
            // Complex lunch with many portions
            {
                name: "Lunch",
                description: "Mediterranean-style lunch with grilled chicken salad, hummus, and pita bread",
                imageUrl: "",
                data: {
                    calories: 685,
                    protein: 52,
                    carbs: 48,
                    fat: 28,
                    fiber: 12,
                    ingredients: ["Chicken breast", "Mixed greens", "Cherry tomatoes", "Cucumber", "Hummus", "Pita bread", "Olive oil", "Feta cheese", "Red onion", "Olives", "Lemon"],
                    portions: [
                        { name: "Grilled chicken breast", amount: "5 oz", calories: 230, protein: 43, carbs: 0, fat: 5 },
                        { name: "Mixed greens", amount: "3 cups", calories: 30, protein: 3, carbs: 6, fat: 0 },
                        { name: "Cherry tomatoes", amount: "1 cup", calories: 30, protein: 2, carbs: 6, fat: 0 },
                        { name: "Cucumber slices", amount: "1 cup", calories: 16, protein: 1, carbs: 4, fat: 0 },
                        { name: "Red onion", amount: "1/4 cup", calories: 15, protein: 0, carbs: 4, fat: 0 },
                        { name: "Kalamata olives", amount: "2 tbsp", calories: 60, protein: 0, carbs: 2, fat: 6 },
                        { name: "Feta cheese", amount: "2 oz", calories: 150, protein: 8, carbs: 2, fat: 12 },
                        { name: "Hummus", amount: "1/4 cup", calories: 140, protein: 8, carbs: 16, fat: 8 },
                        { name: "Whole wheat pita", amount: "1 large", calories: 170, protein: 6, carbs: 35, fat: 2 },
                        { name: "Olive oil & lemon dressing", amount: "2 tbsp", calories: 120, protein: 0, carbs: 2, fat: 14 },
                        { name: "Sparkling water with lemon", amount: "12 oz", calories: 5, protein: 0, carbs: 1, fat: 0 }
                    ],
                    mealType: "lunch",
                    prepTime: 20
                }
            },
            // Simple lunch with few portions
            {
                name: "Lunch", 
                description: "Quick sandwich and soup combo",
                imageUrl: "",
                data: {
                    calories: 420,
                    protein: 28,
                    carbs: 45,
                    fat: 12,
                    fiber: 6,
                    ingredients: ["Turkey", "Bread", "Cheese", "Soup"],
                    portions: [
                        { name: "Turkey sandwich", amount: "1 sandwich", calories: 320, protein: 25, carbs: 30, fat: 8 },
                        { name: "Tomato soup", amount: "1 cup", calories: 90, protein: 3, carbs: 15, fat: 2 },
                        { name: "Water", amount: "16 oz", calories: 0, protein: 0, carbs: 0, fat: 0 }
                    ],
                    mealType: "lunch",
                    prepTime: 10
                }
            },
            // Minimal lunch
            {
                name: "Lunch",
                description: "Simple salad",
                imageUrl: "",
                data: {
                    calories: 250,
                    protein: 8,
                    carbs: 15,
                    fat: 18,
                    fiber: 5,
                    ingredients: ["Greens", "Dressing"],
                    portions: [
                        { name: "Garden salad", amount: "1 large bowl", calories: 150, protein: 6, carbs: 12, fat: 8 },
                        { name: "Ranch dressing", amount: "2 tbsp", calories: 100, protein: 1, carbs: 3, fat: 10 }
                    ],
                    mealType: "lunch",
                    prepTime: 5
                }
            },
            // Complex dinner
            {
                name: "Dinner",
                description: "Balanced dinner with baked salmon, quinoa, roasted vegetables, and side salad",
                imageUrl: "",
                data: {
                    calories: 785,
                    protein: 48,
                    carbs: 68,
                    fat: 32,
                    fiber: 14,
                    ingredients: ["Salmon fillet", "Quinoa", "Broccoli", "Bell peppers", "Mixed greens", "Olive oil", "Lemon", "Avocado", "Sweet potato", "Asparagus"],
                    portions: [
                        { name: "Baked salmon fillet", amount: "6 oz", calories: 336, protein: 34, carbs: 0, fat: 22 },
                        { name: "Herbed quinoa", amount: "1 cup cooked", calories: 220, protein: 8, carbs: 40, fat: 4 },
                        { name: "Roasted broccoli", amount: "1 cup", calories: 55, protein: 4, carbs: 11, fat: 1 },
                        { name: "Roasted bell peppers", amount: "3/4 cup", calories: 25, protein: 1, carbs: 6, fat: 0 },
                        { name: "Roasted sweet potato", amount: "1/2 medium", calories: 60, protein: 1, carbs: 14, fat: 0 },
                        { name: "Grilled asparagus", amount: "6 spears", calories: 20, protein: 2, carbs: 4, fat: 0 },
                        { name: "Mixed greens", amount: "2 cups", calories: 20, protein: 2, carbs: 4, fat: 0 },
                        { name: "Avocado slices", amount: "1/2 medium", calories: 120, protein: 2, carbs: 6, fat: 11 },
                        { name: "Olive oil & herbs", amount: "1 tbsp", calories: 120, protein: 0, carbs: 0, fat: 14 },
                        { name: "Lemon wedges", amount: "2 wedges", calories: 4, protein: 0, carbs: 1, fat: 0 },
                        { name: "Sparkling water", amount: "16 oz", calories: 0, protein: 0, carbs: 0, fat: 0 }
                    ],
                    mealType: "dinner",
                    prepTime: 40
                }
            },
            // Simple dinner
            {
                name: "Dinner",
                description: "Quick pasta with marinara sauce",
                imageUrl: "",
                data: {
                    calories: 480,
                    protein: 18,
                    carbs: 72,
                    fat: 14,
                    fiber: 8,
                    ingredients: ["Pasta", "Marinara sauce", "Parmesan cheese"],
                    portions: [
                        { name: "Whole wheat pasta", amount: "2 cups cooked", calories: 350, protein: 14, carbs: 68, fat: 2 },
                        { name: "Marinara sauce", amount: "1/2 cup", calories: 70, protein: 2, carbs: 12, fat: 2 },
                        { name: "Parmesan cheese", amount: "2 tbsp", calories: 60, protein: 5, carbs: 1, fat: 4 }
                    ],
                    mealType: "dinner",
                    prepTime: 15
                }
            },
            // Complex snack
            {
                name: "Morning Snack",
                description: "Energy snack with Greek yogurt parfait, mixed nuts, and granola",
                imageUrl: "",
                data: {
                    calories: 425,
                    protein: 28,
                    carbs: 48,
                    fat: 18,
                    fiber: 8,
                    ingredients: ["Greek yogurt", "Blueberries", "Strawberries", "Honey", "Almonds", "Walnuts", "Granola", "Chia seeds", "Coconut flakes"],
                    portions: [
                        { name: "Plain Greek yogurt", amount: "1 cup", calories: 150, protein: 20, carbs: 9, fat: 4 },
                        { name: "Fresh blueberries", amount: "1/2 cup", calories: 40, protein: 1, carbs: 10, fat: 0 },
                        { name: "Sliced strawberries", amount: "1/2 cup", calories: 25, protein: 1, carbs: 6, fat: 0 },
                        { name: "Honey drizzle", amount: "1 tbsp", calories: 64, protein: 0, carbs: 17, fat: 0 },
                        { name: "Sliced almonds", amount: "2 tbsp", calories: 70, protein: 3, carbs: 3, fat: 6 },
                        { name: "Chopped walnuts", amount: "1 tbsp", calories: 50, protein: 1, carbs: 1, fat: 5 },
                        { name: "Granola clusters", amount: "3 tbsp", calories: 90, protein: 3, carbs: 16, fat: 3 },
                        { name: "Chia seeds", amount: "1 tsp", calories: 20, protein: 1, carbs: 2, fat: 1 },
                        { name: "Coconut flakes", amount: "1 tbsp", calories: 35, protein: 0, carbs: 1, fat: 3 },
                        { name: "Green tea", amount: "1 cup", calories: 2, protein: 0, carbs: 0, fat: 0 }
                    ],
                    mealType: "snack",
                    prepTime: 10
                }
            },
            // Simple snack
            {
                name: "Afternoon Snack",
                description: "Apple with peanut butter",
                imageUrl: "",
                data: {
                    calories: 270,
                    protein: 8,
                    carbs: 25,
                    fat: 16,
                    fiber: 6,
                    ingredients: ["Apple", "Peanut butter"],
                    portions: [
                        { name: "Medium apple", amount: "1 apple", calories: 95, protein: 0, carbs: 25, fat: 0 },
                        { name: "Natural peanut butter", amount: "2 tbsp", calories: 190, protein: 8, carbs: 6, fat: 16 }
                    ],
                    mealType: "snack",
                    prepTime: 2
                }
            },
            // Minimal snack
            {
                name: "Evening Snack",
                description: "Handful of nuts",
                imageUrl: "",
                data: {
                    calories: 160,
                    protein: 6,
                    carbs: 4,
                    fat: 14,
                    fiber: 3,
                    ingredients: ["Mixed nuts"],
                    portions: [
                        { name: "Mixed nuts", amount: "1 oz", calories: 160, protein: 6, carbs: 4, fat: 14 }
                    ],
                    mealType: "snack",
                    prepTime: 1
                }
            }
        ];
    }

    private getProfileTemplates() {
        return [
            {
                name: "Sarah's Fitness Journey",
                description: "Active lifestyle with focus on strength training and balanced nutrition",
                imageUrl: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=300&fit=crop",
                data: {
                    age: 28,
                    height: 66,
                    weight: 140,
                    activityLevel: "active",
                    goals: ["Build muscle", "Maintain weight", "Increase strength"],
                    restrictions: ["Dairy-free"],
                    targetCalories: 2200,
                    targetProtein: 120
                }
            },
            {
                name: "Mike's Weight Loss Plan",
                description: "Focused on sustainable weight loss through cardio and calorie deficit",
                imageUrl: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=300&fit=crop",
                data: {
                    age: 35,
                    height: 70,
                    weight: 195,
                    activityLevel: "moderate",
                    goals: ["Lose weight", "Improve cardiovascular health"],
                    restrictions: [],
                    targetCalories: 1800,
                    targetProtein: 140
                }
            },
            {
                name: "Emma's Marathon Training",
                description: "Endurance athlete preparing for first marathon",
                imageUrl: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=300&fit=crop",
                data: {
                    age: 26,
                    height: 64,
                    weight: 125,
                    activityLevel: "very_active",
                    goals: ["Improve endurance", "Complete marathon", "Maintain energy"],
                    restrictions: ["Gluten-free"],
                    targetCalories: 2400,
                    targetProtein: 95
                }
            }
        ];
    }

    async getItems(page: number, pageSize: number, options?: QueryOptions): Promise<DataItem[]> {
        if (!this.db) throw new Error('SQL.js not initialized');

        try {
            const offset = (page - 1) * pageSize;
            const { query, params } = this.buildQuery(options);
            
            const stmt = this.db.prepare(`
                SELECT * FROM ${this.storeName}
                ${query}
                LIMIT ? OFFSET ?
            `);

            const items: DataItem[] = [];
            stmt.bind([...params, pageSize, offset]);

            while (stmt.step()) {
                const row = stmt.getAsObject();
                items.push({
                    id: row.id,
                    name: row.name,
                    description: row.description,
                    imageUrl: row.imageUrl,
                    category: row.category,
                    timestamp: row.timestamp,
                    data: row.data ? JSON.parse(row.data) : undefined
                });
            }

            stmt.free();
            return items;
        } catch (error) {
            console.error('Error in getItems:', error);
            // If table doesn't exist, try to create schema and return empty array
            if (error instanceof Error && error.message.includes('no such table')) {
                console.log('Table does not exist, attempting to create schema');
                this.createSchema();
                return [];
            }
            throw error;
        }
    }

    async getTotalItemCount(options?: QueryOptions): Promise<number> {
        console.log('SQLJSProvider.getTotalItemCount() called', { 
            initialized: this.initialized, 
            hasDb: !!this.db, 
            hasSQL: !!this.SQL 
        });
        
        if (!this.db) throw new Error('SQL.js not initialized');

        try {
            const { query, params } = this.buildQuery(options, true);
            
            if (params.length > 0) {
                const stmt = this.db.prepare(`SELECT COUNT(*) as count FROM ${this.storeName} ${query}`);
                stmt.bind(params);
                stmt.step();
                const result = stmt.getAsObject();
                stmt.free();
                return result.count || 0;
            } else {
                const result = this.db.exec(`SELECT COUNT(*) as count FROM ${this.storeName}`);
                return result[0]?.values[0][0] || 0;
            }
        } catch (error) {
            console.error('Error in getTotalItemCount:', error);
            // If table doesn't exist, try to create schema and return 0
            if (error instanceof Error && error.message.includes('no such table')) {
                console.log('Table does not exist, attempting to create schema');
                this.createSchema();
                return 0;
            }
            throw error;
        }
    }

    async getCategories(): Promise<string[]> {
        if (!this.db) throw new Error('SQL.js not initialized');

        try {
            const result = this.db.exec(`SELECT DISTINCT category FROM ${this.storeName} ORDER BY category`);
            return result[0]?.values.map(row => row[0]) || [];
        } catch (error) {
            console.error('Error in getCategories:', error);
            // If table doesn't exist, try to create schema and return empty array
            if (error instanceof Error && error.message.includes('no such table')) {
                console.log('Table does not exist, attempting to create schema');
                this.createSchema();
                return [];
            }
            throw error;
        }
    }

    async clearData(): Promise<void> {
        if (!this.db) throw new Error('SQL.js not initialized');

        this.db.run(`DELETE FROM ${this.storeName}`);
        await this.saveToOPFS();
    }

    async addItem(item: Omit<DataItem, 'id'>): Promise<DataItem> {
        if (!this.db) throw new Error('SQL.js not initialized');

        const id = generateId();
        const stmt = this.db.prepare(`
            INSERT INTO ${this.storeName} (id, name, description, imageUrl, category, timestamp, data)
            VALUES (?, ?, ?, ?, ?, ?, ?)
        `);

        stmt.run([
            id,
            item.name,
            item.description,
            item.imageUrl,
            item.category,
            item.timestamp,
            item.data ? JSON.stringify(item.data) : null
        ]);

        stmt.free();
        await this.saveToOPFS();

        return { ...item, id };
    }

    async updateItem(id: string, item: Omit<DataItem, 'id'>): Promise<DataItem> {
        if (!this.db) throw new Error('SQL.js not initialized');

        const stmt = this.db.prepare(`
            UPDATE ${this.storeName} 
            SET name = ?, description = ?, imageUrl = ?, category = ?, timestamp = ?, data = ?
            WHERE id = ?
        `);

        stmt.run([
            item.name,
            item.description,
            item.imageUrl,
            item.category,
            item.timestamp,
            item.data ? JSON.stringify(item.data) : null,
            id
        ]);

        stmt.free();
        await this.saveToOPFS();

        return { ...item, id };
    }

    async deleteItem(id: string): Promise<void> {
        if (!this.db) throw new Error('SQL.js not initialized');

        const stmt = this.db.prepare(`DELETE FROM ${this.storeName} WHERE id = ?`);
        stmt.run([id]);
        stmt.free();
        await this.saveToOPFS();
    }

    getName(): string {
        return 'SQL.js';
    }

    async getStorageInfo(): Promise<{ used: number; available?: number } | null> {
        try {
            if (this.db) {
                const data = this.db.export();
                const used = data.byteLength;

                if ('storage' in navigator && 'estimate' in navigator.storage) {
                    const estimate = await navigator.storage.estimate();
                    return {
                        used,
                        available: estimate.quota
                    };
                }

                return { used };
            }
        } catch (error) {
            console.error('Failed to get storage info:', error);
        }
        return null;
    }

    /**
     * Export the raw SQLite database
     * @returns The database as a Uint8Array
     */
    async exportDatabase(): Promise<Uint8Array> {
        if (!this.db) throw new Error('SQL.js not initialized');
        return this.db.export();
    }

    private async saveToOPFS(): Promise<void> {
        try {
            // Check if OPFS is supported
            if (!('storage' in navigator) || !('getDirectory' in navigator.storage)) {
                console.warn('OPFS not supported in this browser, data will not persist');
                return;
            }

            const data = this.db.export();
            const root = await navigator.storage.getDirectory();
            const fileHandle = await root.getFileHandle(`${this.dbName}.db`, { create: true });
            
            // Check if createWritable is supported (Chrome/Edge/Firefox)
            if (typeof fileHandle.createWritable === 'function') {
                const writable = await fileHandle.createWritable();
                await writable.write(data);
                await writable.close();
                console.log('Database saved to OPFS using createWritable');
            } else {
                // Fallback: Try createSyncAccessHandle (for Web Workers)
                if (typeof fileHandle.createSyncAccessHandle === 'function') {
                    const accessHandle = await fileHandle.createSyncAccessHandle();
                    accessHandle.write(data, { at: 0 });
                    accessHandle.flush();
                    accessHandle.close();
                    console.log('Database saved to OPFS using createSyncAccessHandle');
                } else {
                    // Last resort: use localStorage for small databases or warn user
                    if (data.length < 5 * 1024 * 1024) { // 5MB limit for localStorage
                        const base64Data = btoa(String.fromCharCode(...data));
                        localStorage.setItem(`${this.dbName}_backup`, base64Data);
                        console.log('Database saved to localStorage as fallback');
                    } else {
                        console.warn('OPFS write operations not supported and database too large for localStorage');
                    }
                }
            }
        } catch (error) {
            console.error('Failed to save to OPFS:', error);
        }
    }

    private async loadFromOPFS(): Promise<Uint8Array | null> {
        try {
            // Check if OPFS is supported
            if (!('storage' in navigator) || !('getDirectory' in navigator.storage)) {
                console.log('OPFS not supported, trying localStorage fallback');
                return this.loadFromLocalStorage();
            }

            const root = await navigator.storage.getDirectory();
            const fileHandle = await root.getFileHandle(`${this.dbName}.db`);
            const file = await fileHandle.getFile();
            const buffer = await file.arrayBuffer();
            console.log('Database loaded from OPFS');
            return new Uint8Array(buffer);
        } catch (error) {
            // File doesn't exist in OPFS, try localStorage fallback
            console.log('OPFS file not found, trying localStorage fallback');
            return this.loadFromLocalStorage();
        }
    }

    private loadFromLocalStorage(): Uint8Array | null {
        try {
            const base64Data = localStorage.getItem(`${this.dbName}_backup`);
            if (base64Data) {
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                console.log('Database loaded from localStorage fallback');
                return bytes;
            }
        } catch (error) {
            console.warn('Failed to load from localStorage:', error);
        }
        return null;
    }
}